import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { SlotPricing, useOrganizationsService } from '@restworld/data-services';
import { CUSTOM_EMPLOYER_BASE_URL, useIsFreemiumUser } from '@restworld/utils-common';

import { useShowOrganizationData } from '../organisations/hooks/use-show-organisation-query';
import { isEmployerNoPermissionToBuySlotModalOpenAtom } from '../employer-companies/modals/employer-no-permission-to-buy-slot-modal';
import { useIsEmployerAnAdmin } from '@restworld/feature-authentication';
import { globalDiscountAtom } from '.';
import { activeSelectionSubscriptionItemAtom } from './subscription-checkout';

const queryKey = ['get-flexible-slot-prices'];
export const useFetchFlexibleSlotsPricesQuery = () => {
  const organizationService = useOrganizationsService();
  return useQuery({
    queryKey,
    queryFn: async () => (await organizationService.getFlexibleSlotPrices()).data,
  });
};
export const useFetchFlexibleSlotsPrices = () =>
  (useQueryClient().getQueryData(queryKey) as SlotPricing[])?.map((v) => ({
    no_of_slots: v.no_of_slots,
    price: v.price / 100,
  })) ?? [];
export const useFetchFlexibleSlotsPricesLoading = () => useQueryClient().getQueryState(queryKey).status === 'pending';

export function useProductFunctionalitiesData() {
  const { t } = useTranslation('product-description-page');

  return [
    {
      content: t('public_visibility_of_the_premises'),
      freeTier: true,
      paidTier: true,
    },
    {
      content: t('spontaneous_applications'),
      freeTier: true,
      paidTier: true,
    },
    {
      content: t('shareable_public_link_bio_qr_website'),
      freeTier: true,
      paidTier: true,
    },
    {
      content: t('statistics_on_visits_and_applications'),
      freeTier: true,
      paidTier: true,
    },

    {
      content: t('autonomous_modification_of_the_card'),
      freeTier: true,
      paidTier: true,
    },
    {
      content: t('opening_job_offers'),
      freeTier: false,
      paidTier: true,
    },
    {
      content: t('guided_activation_with_initial_brief'),
      freeTier: false,
      paidTier: true,
    },
    {
      content: t('selected_and_qualified_profiles'),
      freeTier: false,
      paidTier: true,
    },
    {
      content: t('support_from_a_recruiter'),
      freeTier: false,
      paidTier: true,
    },
    {
      content: t('automatic_matching_with_candidates'),
      freeTier: false,
      paidTier: true,
    },
    {
      content: t('minimum_guarantee_five_profiles'),
      freeTier: false,
      paidTier: true,
      noBottomBorder: true,
    },
    {
      content: t('sponsorship_budget_included'),
      freeTier: false,
      paidTier: true,
      noBottomBorder: true,
    },
  ];
}

export function useBuySlot() {
  const organizationData = useShowOrganizationData();
  const isAdmin = useIsEmployerAnAdmin();
  const setIsEmployerNoPermissionToBuySlotModalOpen = useSetAtom(isEmployerNoPermissionToBuySlotModalOpenAtom);
  const globalDiscount = useAtomValue(globalDiscountAtom);
  const itemSelected = useAtomValue(activeSelectionSubscriptionItemAtom);

  const buySlot = useCallback(
    ({ slots, companyId, openNewPage = true }: { slots: number; companyId: string; openNewPage?: boolean }) => {
      if (!isAdmin) {
        setIsEmployerNoPermissionToBuySlotModalOpen(true);
        return;
      }
      const baseUrl = `${CUSTOM_EMPLOYER_BASE_URL}/purchase/flexible-slots`;
      const params = [
        `companyId=${companyId || organizationData?.companies?.[0]?.id}`,
        `slots=${slots}`,
        'autonomous=true',
        globalDiscount?.discount > 0 ? `coupon=${globalDiscount.coupon}` : null,
        itemSelected === 'trial' ? 'product=trial' : null,
      ]
        .filter(Boolean)
        .join('&');
      const url = `${baseUrl}?${params}`;
      if (openNewPage) {
        window?.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // Sostituisce l'indirizzo corrente senza aprire una nuova scheda
        try {
          window?.location?.assign(url);
        } catch (e) {
          // fallback in caso di restrizioni
          window.location.href = url;
        }
      }
    },
    [isAdmin, organizationData?.companies, setIsEmployerNoPermissionToBuySlotModalOpen, globalDiscount, itemSelected]
  );

  return {
    buySlot,
  };
}
